import { Contract, MaxUint256, type BrowserProvider, type JsonRpcSigner } from 'ethers'
import { ROUTER_ABI } from '../constants/abis'

/** Matches KavachRouter.ExactInputSingleParams (tuple as object). */
export interface RouterExactInputSingleParams {
  tokenIn: string
  tokenOut: string
  fee: number
  recipient: string
  deadline: bigint
  amountIn: bigint
  amountOutMinimum: bigint
  sqrtPriceLimitX96: bigint
  ammKind: number
}

export interface RouterExactOutputSingleParams {
  tokenIn: string
  tokenOut: string
  fee: number
  recipient: string
  deadline: bigint
  amountOut: bigint
  amountInMaximum: bigint
  sqrtPriceLimitX96: bigint
  ammKind: number
}

/**
 * On-chain quote via eth_call. KavachRouter pulls tokens from `from`, so this only succeeds when
 * `from` has sufficient balance + allowance for the swap (same limitation as Uniswap-style routers).
 */
export async function quoteExactInputSingleStatic(
  provider: BrowserProvider,
  routerAddress: string,
  from: string,
  params: RouterExactInputSingleParams,
): Promise<bigint | null> {
  const router = new Contract(routerAddress, ROUTER_ABI, provider)
  try {
    const out = await router.exactInputSingle.staticCall(params, { from })
    return out as bigint
  } catch {
    return null
  }
}

export async function quoteExactOutputSingleStatic(
  provider: BrowserProvider,
  routerAddress: string,
  from: string,
  params: RouterExactOutputSingleParams,
): Promise<bigint | null> {
  const router = new Contract(routerAddress, ROUTER_ABI, provider)
  try {
    const amountIn = await router.exactOutputSingle.staticCall(params, { from })
    return amountIn as bigint
  } catch {
    return null
  }
}

/** Simulate exact-out with a high max input so slippage cap does not revert the call. */
export async function quoteExactOutputSingleStaticLoose(
  provider: BrowserProvider,
  routerAddress: string,
  from: string,
  base: Omit<RouterExactOutputSingleParams, 'amountInMaximum'>,
): Promise<bigint | null> {
  return quoteExactOutputSingleStatic(provider, routerAddress, from, {
    ...base,
    amountInMaximum: MaxUint256,
  })
}

export async function estimateSwapGas(
  signer: JsonRpcSigner,
  routerAddress: string,
  params: RouterExactInputSingleParams | RouterExactOutputSingleParams,
  mode: 'in' | 'out',
): Promise<bigint | null> {
  const router = new Contract(routerAddress, ROUTER_ABI, signer)
  try {
    if (mode === 'in') {
      const tx = await router.exactInputSingle.populateTransaction(params as RouterExactInputSingleParams)
      return await signer.estimateGas(tx)
    }
    const tx = await router.exactOutputSingle.populateTransaction(params as RouterExactOutputSingleParams)
    return await signer.estimateGas(tx)
  } catch {
    return null
  }
}
